import { PreBoosterTutorialLayout } from '../../../../Layouts/PreBoosterTutorialLayout';
import { LevelPopupBooster } from './LevelPopupBooster';
import TextComponent from '../../../../Component/Render/Text';
import Game from '../../../../Game';
import GameObject from '../../../../GameObject/GameObject';
import Sprite from '../../../../Prototype/Sprite';
import SpriteComponent from '../../../../Component/Render/Sprite';
import TransformComponent from '../../../../Component/Transform';
import Renderable from '../../../../Component/Renderable';
import { Anim, StartAnimationClip } from '../../../Animation/AnimationHelper';
import { Easing } from '@tweenjs/tween.js';
import { promisifySignal, sleep } from '../../../../Utility/async';
import { DragonbonesDefault } from '../../../Animation/Clip';
import { PlayerActions } from '../../../../State/Player/PlayerActions';
import { initialPlayerInventory } from '../../../../State/Player/initialPlayerStates';
import { PreBoosterType } from 'State/Card/BoosterType';
import { Layout } from '../../../../Utility/layout/createLayout';

type Item = {
    booster: LevelPopupBooster;
    boosterParent: GameObject;
    arrow: GameObject<PIXI.Sprite>;
}

type PreBoosterTutorialProps = {
    boosters: LevelPopupBooster[];
};

export class PreBoosterTutorial {
    private readonly layout: any;
    private readonly items: Item[];

    constructor(props: PreBoosterTutorialProps) {
        this.layout = new PreBoosterTutorialLayout();

        this.items = props.boosters.map((booster) => {
            const arrow = GameObject.Instantiate(Sprite, {});
            const arrowTransform = arrow.getComponent(TransformComponent);
            const arrowSprite = arrow.getComponent(SpriteComponent);
            const boosterTransform = booster.root.getComponent(TransformComponent);
            arrow.addComponent(Renderable);
            arrowTransform.x = boosterTransform.x;
            arrowTransform.y = boosterTransform.y - 40;
            arrowSprite.texture = 'info_arrow';
            arrowSprite.anchor.set(0.5, 1);
            this.layout.arrowsContainer.addChild(arrow);

            return { booster, arrow, boosterParent: booster.root.parent };
        });

        let dialogText: string;
        if (this.items.length === 1) {
            dialogText = Game.locale.getText('YouUnlockedABooster');
            switch (this.items[0].booster.type) {
                case PreBoosterType.REMOVE_THREE:
                    dialogText = dialogText.replace('[x]', Game.locale.getText('RemoveThreePreBoosterName'));
                    break;
                case PreBoosterType.JOKER:
                    dialogText = dialogText.replace('[x]', Game.locale.getText('JokerPreBoosterName'));
                    break;
                case PreBoosterType.CANISTER:
                    dialogText = dialogText.replace('[x]', Game.locale.getText('CanisterPreBoosterName'));
                    break;
            }
        } else {
            dialogText = Game.locale.getText('WelcomeBoosters');
        }

        this.layout.dialogText.getComponent(TextComponent).text = dialogText;

        this.show();
    }

    public static tryToShowBoostersTutorial(props: { boosters: LevelPopupBooster[], parent: GameObject }): void {
        const state = Game.store.getState();

        if(!state.playerState.unlockedBoosters) {
            state.playerState.unlockedBoosters = []; // hack fix
        }

        const boosters = props.boosters.filter((booster) => {
            return (
                !state.playerState.unlockedBoosters.includes(booster.type) &&
                !booster.isLocked()
            );
        });

        PlayerActions.setPlayerState({
            ...state.playerState,
            inventory: {
                ...(
                    boosters.reduce((acc, booster) => {
                        acc[booster.type] = acc[booster.type] > 0 ? acc[booster.type] : initialPlayerInventory[booster.type];
                        return acc;
                    }, { ...state.playerState.inventory })
                ),
            },
            unlockedBoosters: [...state.playerState.unlockedBoosters, ...boosters.map((b) => b.type)],
        });

        boosters.forEach((booster) => {
            booster.update();
        });

        if (boosters.length !== 0) {
            const tutorial = new PreBoosterTutorial({ boosters });
            props.parent.addChild(tutorial.root);
        }
    }

    public get root(): GameObject {
        return this.layout.root;
    }

    private async show(): Promise<void> {
        this.layout.overlay.getComponent(Renderable).alpha = 0;
        this.layout.arrowsContainer.getComponent(Renderable).alpha = 0;
        this.layout.dialogContainer.getComponent(TransformComponent).y -= 400;

        await sleep(100);

        Anim(this.layout.overlay, { alpha: 1, duration: 500 });
        Anim(this.layout.arrowsContainer, { alpha: 1, duration: 500 });
        Anim(this.layout.dialogContainer, { y: '+400', duration: 500, easing: Easing.Sinusoidal.Out });

        this.items.map(async (item) => {
            item.boosterParent = item.booster.root.parent;
            this.layout.boostersContainer.addChild(item.booster.root);

            Anim(item.arrow, {
                y: item.arrow.getComponent(TransformComponent).y + 20,
                duration: 500,
                repeatCount: Infinity,
                easing: Easing.Sinusoidal.InOut,
                yoyo: true,
            });

            const sparkles = item.booster.getSparkles();
            await Anim(sparkles, { alpha: 1, duration: 500 });
            StartAnimationClip(new DragonbonesDefault(sparkles, { animationName: 'animtion0', playTimes: 1 }));
        });

        await Promise.race(this.items.map((item) => promisifySignal(item.booster.onClick)));
        this.hide();
    }

    private async hide(): Promise<void> {
        await Anim(this.layout.overlay, { alpha: 0, duration: 300 });

        this.items.map(async (item) => {
            item.boosterParent.addChild(item.booster.root);

            await Anim(item.booster.getSparkles(), { alpha: 0, duration: 300 });
        });

        await Anim(this.layout.root, { alpha: 0, duration: 300 });
        this.layout.root.destroy();
    }
}
